import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

type GridQuerySyncConfig = {
  // Keys whose value is an array (serialized as repeated params, e.g. ?store_ids=1&store_ids=2).
  arrayKeys?: string[];
  // Keys whose value must be coerced back to a number when read from the URL (e.g. page, limit).
  numberKeys?: string[];
};

const isEmpty = (value: unknown) => value === undefined || value === null || value === '';

const queryToSearchParams = (query: Record<string, unknown>) => {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (isEmpty(value)) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (!isEmpty(item)) params.append(key, String(item));
      });
      return;
    }

    params.set(key, String(value));
  });

  return params;
};

const searchParamsToQuery = (
  searchParams: URLSearchParams,
  { arrayKeys = [], numberKeys = [] }: GridQuerySyncConfig,
) => {
  const query: Record<string, unknown> = {};

  new Set(searchParams.keys()).forEach((key) => {
    if (arrayKeys.includes(key)) {
      query[key] = searchParams.getAll(key);
      return;
    }

    const value = searchParams.get(key);
    if (value === null) return;

    query[key] = numberKeys.includes(key) ? Number(value) : value;
  });

  return query;
};

/**
 * Keeps a grid's Redux `query` (filters, sorting, search, pagination) in sync with the URL query string.
 *
 * - On mount the state is restored from the URL, so returning to the list (e.g. via "Back" from a detail
 *   card) lands on the exact same page with the same filters/sorting/search.
 * - Every subsequent change to `query` is reflected back into the URL (using `replace`, so navigating into a
 *   detail card stays a single history entry).
 * - Opening the list fresh from the menu (no query string) restores an empty query and therefore starts clean.
 */
export const useGridQuerySync = <T extends Record<string, unknown>>(
  query: T,
  onRestore: (restoredQuery: Partial<T>) => void,
  config: GridQuerySyncConfig = {},
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isFirstSync = useRef(true);

  // Restore the grid state from the URL once, on mount.
  useEffect(() => {
    onRestore(searchParamsToQuery(searchParams, config) as Partial<T>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect every subsequent query change into the URL. The very first run is skipped so that the freshly
  // restored URL is not overwritten with the (still stale) previous query value.
  useEffect(() => {
    if (isFirstSync.current) {
      isFirstSync.current = false;
      return;
    }

    setSearchParams(queryToSearchParams(query), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
};

export default useGridQuerySync;
